import { useQuery } from "@tanstack/react-query";

import { apiFetch, ApiError } from "@/lib/api";
import {
  formatNumber,
  mrpLocationChipStyles,
  type MrpIncomingFeedResponse,
  type MrpLocation,
} from "@/lib/mrp";
import { cn } from "@/lib/utils";

import {
  INCOMING_PEEK_ROW_LIMIT,
  filterIncomingPeekRows,
  incomingPeekDocumentLabel,
  incomingPeekExpectedLabel,
  incomingPeekKindLabel,
} from "./mrpIncomingPeekModel";

export function MrpIncomingPeekHoverHintBody({
  cpn,
  location,
}: {
  cpn: string;
  /** Operational warehouse code. When set, peek is incoming to that origin site. */
  location?: string | null;
}): JSX.Element {
  const locationCode = location?.trim().toLowerCase() || null;
  const incomingQuery = useQuery<MrpIncomingFeedResponse, ApiError>({
    queryKey: ["mrp", "incoming", "peek", cpn, locationCode ?? ""],
    queryFn: () =>
      apiFetch<MrpIncomingFeedResponse>("/api/mrp/incoming", {
        query: {
          cpn,
          include_sku_hints: "0",
          ...(locationCode != null ? { location: locationCode } : {}),
        },
      }),
    staleTime: 60_000,
    refetchOnWindowFocus: false,
  });

  if (incomingQuery.isLoading) {
    return <span className="text-slate-600">Loading incoming…</span>;
  }

  if (incomingQuery.isError) {
    return <span className="text-red-700">{incomingQuery.error.message}</span>;
  }

  const rows = filterIncomingPeekRows(incomingQuery.data?.rows ?? [], locationCode);
  if (rows.length === 0) {
    return (
      <span className="text-slate-600">
        {locationCode != null
          ? `No open incoming to ${locationCode.toUpperCase()} for this SKU.`
          : "No open incoming for this SKU."}
      </span>
    );
  }

  const visibleRows = rows.slice(0, INCOMING_PEEK_ROW_LIMIT);
  const hiddenCount = rows.length - visibleRows.length;

  return (
    <div className="w-max max-w-[20rem] space-y-1.5">
      <div className="border-b border-slate-100 pb-1 text-xs font-medium text-slate-800">
        {locationCode != null
          ? `Incoming to ${locationCode.toUpperCase()} for ${cpn}`
          : `Incoming for ${cpn}`}
      </div>
      <ul className="space-y-1 text-[11px] leading-snug text-slate-700">
        {visibleRows.map((row) => {
          const locationChip = mrpLocationChipStyles(row.location_code as MrpLocation);
          return (
            <li
              key={`${row.kind}-${row.id}-${row.delivery_schedule_id ?? "base"}`}
              className="grid grid-cols-[auto_1fr_auto] gap-x-2"
            >
              <span className="font-medium text-slate-500">{incomingPeekKindLabel(row)}</span>
              <span className="truncate font-mono text-slate-800">{incomingPeekDocumentLabel(row)}</span>
              <span className="text-right tabular-nums text-slate-900">{formatNumber(row.quantity_remaining)}</span>
              <span className="col-span-3 flex items-center gap-2 pl-0.5 text-slate-500">
                {locationCode == null ? (
                  <span
                    className={cn(
                      "inline-flex shrink-0 rounded border px-1.5 py-0 text-[10px] font-medium",
                      locationChip.className,
                    )}
                  >
                    {locationChip.label}
                  </span>
                ) : null}
                <span>{incomingPeekExpectedLabel(row.expected_arrival)}</span>
              </span>
            </li>
          );
        })}
      </ul>
      {hiddenCount > 0 ? (
        <p className="text-[11px] text-slate-500">{hiddenCount} more on Incoming parts.</p>
      ) : null}
    </div>
  );
}
